import React, { useState } from 'react';
import styled from 'styled-components';
import { FiEdit2, FiTarget, FiFlag, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import theme from '../../styles/theme';

const CardContainer = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary[300]};
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[5]};
  background: ${theme.colors.background.tertiary};
  cursor: pointer;

  &:hover {
    background: ${theme.colors.background.secondary};
  }
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const FunilIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.primary[500]}20;
  color: ${theme.colors.primary[500]};
  font-size: ${theme.fontSizes.xl};
`;

const FunilInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const FunilNome = styled.h3`
  margin: 0;
  font-size: ${theme.fontSizes.lg};
  font-weight: 700;
  color: ${theme.colors.text.primary};
`;

const FunilMeta = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.muted};
`;

const ExpandIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.xl};
  transition: transform 0.2s ease;
`;

const EtapasList = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${theme.colors.border.primary};
`;

const EtapaItem = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: ${theme.spacing[4]} ${theme.spacing[5]};
  border-bottom: 1px solid ${theme.colors.border.primary};
  gap: ${theme.spacing[4]};
  min-width: 0;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${theme.colors.background.primary}50;
  }
`;

const EtapaInfo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
  flex: 1;
  min-width: 0;
`;

const EtapaOrdem = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 700;
`;

const EtapaNome = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.text.primary};
  font-weight: 600;
  font-size: ${theme.fontSizes.base};
  min-width: 0;
`;

const EtapaNomeTexto = styled.span`
  flex-shrink: 1;
  min-width: 0;
  word-break: break-word;
`;

const FlagIcon = styled.div`
  color: ${props => props.$ativo ? theme.colors.success[500] : theme.colors.error[500]};
  font-size: ${theme.fontSizes.base};
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${theme.spacing[2]};
  flex-shrink: 0;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.primary[500]};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  font-weight: 600;
  font-size: ${theme.fontSizes.sm};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${theme.colors.primary[600]};
    border-color: ${theme.colors.primary[600]};
  }
`;

const FunilMensagensCard = ({ funil }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(true);

  const handleEdit = (mensagemId) => {
    if (!mensagemId) return;
    navigate(`/leads/mensagens-etapa/${mensagemId}/editar`);
  };

  // Ordenar mensagens por ordem da etapa
  const mensagensOrdenadas = [...(funil.mensagens || [])].sort((a, b) => {
    const ordemA = a.etapa_ordem || 0;
    const ordemB = b.etapa_ordem || 0;
    return ordemA - ordemB;
  });

  const ativasCount = mensagensOrdenadas.filter(m => m.ativa).length;
  
  // Contar canais únicos
  const canaisUnicos = new Set(
    mensagensOrdenadas
      .map(m => m.canal_nome || m.canal)
      .filter(canal => canal)
  );
  const canaisCount = canaisUnicos.size;

  return (
    <CardContainer>
      <Header onClick={() => setExpanded(!expanded)}>
        <TitleGroup>
          <FunilIcon>
            <FiTarget />
          </FunilIcon>
          <FunilInfo>
            <FunilNome>{funil.funil_nome}</FunilNome>
            <FunilMeta>
              {mensagensOrdenadas.length} {mensagensOrdenadas.length === 1 ? 'etapa configurada' : 'etapas configuradas'} • {ativasCount} {ativasCount === 1 ? 'ativa' : 'ativas'} • {canaisCount} {canaisCount === 1 ? 'canal' : 'canais'}
            </FunilMeta>
          </FunilInfo>
        </TitleGroup>
        <ExpandIcon>
          {expanded ? <FiChevronUp /> : <FiChevronDown />}
        </ExpandIcon>
      </Header>

      {expanded && (
        <EtapasList>
          {mensagensOrdenadas.map((mensagem) => (
            <EtapaItem key={mensagem.id}>
              <EtapaInfo>
                <EtapaOrdem>{mensagem.etapa_ordem || '?'}</EtapaOrdem>
                <EtapaNome>
                  <FlagIcon $ativo={mensagem.ativa}>
                    <FiFlag />
                  </FlagIcon>
                  <EtapaNomeTexto>{mensagem.etapa_nome || 'Etapa'}</EtapaNomeTexto>
                </EtapaNome>
              </EtapaInfo>
              <ActionsContainer>
                <ActionButton type="button" onClick={() => handleEdit(mensagem.id)}>
                  <FiEdit2 />
                  Editar
                </ActionButton>
              </ActionsContainer>
            </EtapaItem>
          ))}
        </EtapasList>
      )}
    </CardContainer>
  );
};

export default FunilMensagensCard;

